import React from 'react';

import PropTypes from 'prop-types';

import { Body } from '@iris/react-components';

import { getSpecIcon } from '@/containers/vdp/VehicleFeaturesContainer/VehicleFeatureHighlights/utils';

import ColorDisplay from './ColorDisplay';
import DifferentiatorsDisplay from './DifferentiatorsDisplay';

/**
 * Renders color display if the spec is a color type and has color data
 */
const renderColorDisplay = (spec, exteriorColorData, interiorColorData) => {
    const colorSpecs = {
        exterior: { type: 'exterior', colorData: exteriorColorData },
        interiorSeats: { type: 'interior', colorData: interiorColorData },
    };

    const colorSpec = colorSpecs[spec.key];

    if (colorSpec?.colorData?.colorVariant) {
        return (
            <ColorDisplay
                type={colorSpec.type}
                colorValue={colorSpec.colorData.colorValue}
                colorDescription={colorSpec.colorData.colorDescription}
            />
        );
    }

    return null;
};

/**
 * Renders the appropriate content for a spec based on its key and value
 */
const renderSpecContent = (spec, displayValue) => {
    // Handle grouped differentiators only
    if (spec.key === 'differentiators') {
        const differentiators = Array.isArray(displayValue) ? displayValue : [displayValue];
        return (
            <DifferentiatorsDisplay
                differentiators={differentiators}
            />
        );
    }

    if (spec.key === 'windowSticker' && React.isValidElement(displayValue)) {
        // Window Sticker comes as a LinkIcon component, render it directly with text-link styling
        return (
            <Body
                as="span"
                className="text-link text-nowrap link-undecorated"
            >
                {displayValue}
            </Body>
        );
    }

    return (
        <Body
            as="span"
            className="no-wrap"
        >
            {displayValue}
        </Body>
    );
};

// Skip individual differentiator specs - these are handled by the grouped 'differentiators' spec
const individualDifferentiatorKeys = new Set(['lowMileage', 'wellEquipped', 'vehicleReconditioning']);

/**
 * Component to render a single spec item with appropriate icon and content
 * @param {Object} props
 * @param {Object} props.spec - The spec object

 * @param {Object} props.exteriorColorData - Exterior color data
 * @param {Object} props.interiorColorData - Interior color data
 */
function SpecItem({ spec, exteriorColorData, interiorColorData }) {
    if (individualDifferentiatorKeys.has(spec?.key)) {
        return null;
    }

    // Handle color specs (exterior and interior)
    const colorDisplay = renderColorDisplay(spec, exteriorColorData, interiorColorData);

    if (colorDisplay) {
        return colorDisplay;
    }

    const icon = getSpecIcon(spec.key);

    // Check if we have a valid spec value to display
    if (icon && spec.specData?.value) {
        const displayValue = spec.specData.value;

        // Only render if we have a valid displayValue
        if (!displayValue
            || (typeof displayValue === 'string' && displayValue?.trim() === '')
            || (Array.isArray(displayValue) && displayValue?.length === 0)) {
            return null;
        }

        return (
            <div
                key={spec.key}
                className="iris:flex iris:items-center iris:gap-2"
            >
                <span className="features-card-icon-primary">
                    {icon}
                </span>
                {renderSpecContent(spec, displayValue)}
            </div>
        );
    }

    return null;
}

SpecItem.propTypes = {
    spec: PropTypes.shape({
        key: PropTypes.string.isRequired,
        specData: PropTypes.shape({
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.array,
                PropTypes.object,
            ]),
        }),
    }).isRequired,
    exteriorColorData: PropTypes.shape({
        colorVariant: PropTypes.string,
        colorValue: PropTypes.string,
        colorDescription: PropTypes.string,
    }).isRequired,
    interiorColorData: PropTypes.shape({
        colorVariant: PropTypes.string,
        colorValue: PropTypes.string,
        colorDescription: PropTypes.string,
    }).isRequired,
};

export default SpecItem;
